import { z } from 'zod';

/**
 * Cuotas de almacenamiento.
 *
 * Una cuota se fija sobre una carpeta y limita todo lo que cuelga de ella. Se
 * cuentan también las versiones antiguas y lo que está en la papelera: ocupan
 * disco igual, y si no se contaran, "vaciar la papelera" no liberaría nada y
 * nadie entendería por qué.
 */

/** Desde acá se avisa. Ochenta por ciento es la convención y da tiempo a reaccionar. */
export const UMBRAL_ALERTA = 0.8;

export type EstadoCuota = 'SIN_CUOTA' | 'OK' | 'ALERTA' | 'LLENA';

export interface UsoDeCarpeta {
  nodoId: string;
  nombre: string;
  /** Ruta legible, para distinguir dos carpetas "Contratos" en ramas distintas. */
  ruta: string;
  cuotaBytes: number | null;
  usadoBytes: number;
  /** De lo usado, cuánto está en la papelera: es lo que se puede recuperar hoy. */
  papeleraBytes: number;
  archivos: number;
  versiones: number;
  /** null cuando no hay cuota: no se puede calcular un porcentaje de infinito. */
  porcentaje: number | null;
  libreBytes: number | null;
  estado: EstadoCuota;
}

export interface CuotaDeNodo {
  /** La cuota fijada en esta carpeta, si tiene una propia. */
  propia: UsoDeCarpeta | null;
  /**
   * La que realmente limita: de todas las cuotas de la cadena de ancestros, la
   * que deja menos espacio libre. Es la que hace fallar una subida.
   */
  efectiva: UsoDeCarpeta | null;
}

export function estadoDeCuota(usadoBytes: number, cuotaBytes: number | null): EstadoCuota {
  if (cuotaBytes === null) return 'SIN_CUOTA';
  if (usadoBytes >= cuotaBytes) return 'LLENA';
  if (usadoBytes / cuotaBytes >= UMBRAL_ALERTA) return 'ALERTA';
  return 'OK';
}

export const ETIQUETA_ESTADO_CUOTA: Record<EstadoCuota, string> = {
  SIN_CUOTA: 'Sin límite',
  OK: 'Con espacio',
  ALERTA: 'Por llenarse',
  LLENA: 'Sin espacio',
};

/**
 * La cuota se pide en GB con decimales —es como la gente piensa el espacio— y
 * se guarda en bytes. `null` quita el límite.
 */
export const esquemaCuota = z.object({
  cuotaGB: z
    .number({ invalid_type_error: 'Indica el tamaño en GB' })
    .positive('La cuota debe ser mayor que cero')
    .max(1024 * 100, 'El máximo son 102.400 GB')
    .nullable(),
});

export const GB = 1024 * 1024 * 1024;
export const bytesDesdeGB = (gb: number): number => Math.round(gb * GB);
export const gbDesdeBytes = (bytes: number): number => bytes / GB;
